import Big from 'big.js';
import _ from 'lodash';

import * as graphql from 'generated/graphql';
import { currencyAndDecimalFieldPolicy } from './fieldPolicies';
import { ITypeSpecificPolicy } from './shared';



export const EconomicIndex: ITypeSpecificPolicy<graphql.EconomicIndex> = {
  fields: {
    percentChangeVsPriorMonth: currencyAndDecimalFieldPolicy(),
    percentChangeVsPriorYear: currencyAndDecimalFieldPolicy(),
    absoluteChangeVsPriorMonth: currencyAndDecimalFieldPolicy(),
    absoluteChangeVsPriorYear: currencyAndDecimalFieldPolicy(),



    displayName: {
      read: (existing, { readField }) => {
        if (process.env.REACT_APP_DISPLAY_FRED_CODE === 'true') {
          const fredCode = readField<graphql.IndexFredCode>('fredCode');
          return `${existing.slice(0, 20)}(${fredCode})`;
        }
        return existing;
      }
    },



    displayUnit: {
      read: (existing, { readField }) => {
        const result = readField<graphql.IndexFredCode>('fredCode');
        if (result && _.isString(result)) {
          return __fredCode2CalculatedUnit[result];
        }
        return existing;
      }
    },



    mostRecentValue: {
      read: (existing, { readField }) => {
        const val = readField('historicalData');
        const fredCode = readField<graphql.IndexFredCode>('fredCode');
        if (fredCode && _.isArray(val)) {
          const mostRecent = _.last(val ?? []);
          const value = Big(mostRecent.value);
          return __convertMostRecentValue(fredCode, value);
        }

        return Big(existing ?? 0);
      }
    },



    mostRecentChange: {
      read: (existing, { readField }) => {
        const fredCode = readField<graphql.IndexFredCode>('fredCode');
        if (!fredCode) {
          return existing;
        }

        const percentChangeVsPriorMonth = readField<Big>('percentChangeVsPriorMonth');
        const percentChangeVsPriorYear = readField<Big>('percentChangeVsPriorYear');
        const absoluteChangeVsPriorMonth = readField<Big>('absoluteChangeVsPriorMonth');
        const absoluteChangeVsPriorYear = readField<Big>('absoluteChangeVsPriorYear');

        const converted = __convertRecentChange(fredCode as graphql.IndexFredCode, {
          percentChangeVsPriorMonth,
          percentChangeVsPriorYear,
          absoluteChangeVsPriorMonth,
          absoluteChangeVsPriorYear
        });

        const value = converted ?? existing;

        if (fredCode === 'ICSA' || fredCode === 'CCSA' || fredCode === 'EXHOSLUSM495S') {
          return value.div(1000);
        }

        if (__isPercentage(fredCode)) {
          return value.mul(100);
        }

        return value;
      }
    },

    changeDisplayUnit: {
      read: (existing, { readField }) => {
        const fredCode = readField<graphql.IndexFredCode>('fredCode');
        if (fredCode) {
          const isMonthly = __displaysMonthlyChange(fredCode);
          const isAbsolute = __displaysAbsoluteChange(fredCode);
          let symbol = __fredCode2CalculatedUnit[fredCode];
          if (isAbsolute && symbol === '%') {
            symbol = '';
          }
          return `${isAbsolute ? symbol : '%'} ${isMonthly ? 'month' : 'year'}`;
        }

        return existing;
      }
    },



    fredUrl: {
      read: (existing, { readField }) => {
        const result = readField<graphql.IndexFredCode>('fredCode');
        if (!result) {
          return existing;
        }

        const fredHostUrl = 'https://fred.stlouisfed.org';
        if (result in __fredCode2QueryString) {
          return `${fredHostUrl}/graph/${__fredCode2QueryString[result]}`;
        } else {
          return `${fredHostUrl}/series/${result}`;
        }
      }
    }
  }
};



function __displaysAbsoluteChange(fredCode: graphql.IndexFredCode) {
  switch (fredCode) {
    case 'CCSA':
    case 'ICSA':
    case 'CIVPART':

    case 'CPIAUCSL':
    case 'CPILFESL':
    case 'PCEPI':
    case 'PCEPILFE':
    case 'WPUFD49207':
    case 'WPSFD4131':

    case 'PAYEMS':
    case 'PCE':
    case 'PSAVERT':
    case 'U6RATE':
    case 'UNRATE':
    case 'CSUSHPISA':
    case 'JTSJOL':
    case 'DSPIC96':
    case 'MORTGAGE30US':
    case 'CES0500000003':

    case 'HOUST':
    case 'PERMIT':
    case 'EXHOSLUSM495S':
    case 'HPIPONM226S':
    case 'CSUSHPISA':

    // case 'ADXTNO':
    // case 'DGORDER':
    case 'INDPRO':
    case 'OPHNFB':
    case 'TCU':
    // case 'WHLSLRIMSA':
      return true;

    default: return false;
  }
}



function __displaysMonthlyChange(fredCode: graphql.IndexFredCode) {
  switch (fredCode) {

    case 'ADXTNO':
    case 'CCSA':
    case 'CES0500000003':
    case 'CIVPART':
    case 'CPIAUCSL':
    case 'CPILFESL':
    case 'CSUSHPISA':
    case 'DGORDER':

    case 'DSPIC96':
    case 'EXHOSLUSM495S':
    case 'HOUST':
    case 'HPIPONM226S':
    case 'ICSA':
    case 'INDPRO':
    case 'JTSJOL':

    case 'PAYEMS':
    case 'PCE':
    case 'PCEPI':
    case 'PCEPILFE':
    case 'PERMIT':
    case 'PSAVERT':
    case 'RSAFS':
    case 'U6RATE':
    case 'UMCSENT':
    case 'UNRATE':
    case 'TCU':
    case 'WPSFD4131':
    case 'WPUFD49207':
    case 'WHLSLRIMSA':
      return true;
    default:
      return false;
  }
}



function __convertRecentChange(fredCode: graphql.IndexFredCode, {
  percentChangeVsPriorMonth, percentChangeVsPriorYear, absoluteChangeVsPriorMonth, absoluteChangeVsPriorYear
}: {
  percentChangeVsPriorMonth: Big | nil;
  percentChangeVsPriorYear: Big | nil;
  absoluteChangeVsPriorMonth: Big | nil;
  absoluteChangeVsPriorYear: Big | nil;
}) {
  if (__displaysAbsoluteChange(fredCode)) {
    return __displaysMonthlyChange(fredCode)
      ? absoluteChangeVsPriorMonth
      : absoluteChangeVsPriorYear;
  }

  return __displaysMonthlyChange(fredCode)
    ? percentChangeVsPriorMonth?.mul(100)
    : percentChangeVsPriorYear?.mul(100);
}



function __convertMostRecentValue(fredCode: graphql.IndexFredCode, value: Big) {
  switch (fredCode) {
    case 'PCE':
    case 'PCEPI':
    case 'PCEPILFE':
    case 'WPUFD49207':
    case 'WPSFD4131':
    case 'CPIAUCSL':
    case 'CPILFESL':
    case 'DSPIC96':
    case 'CES0500000003':
    case 'CSUSHPISA':
      return value.mul(100);

    case 'ADXTNO':
    case 'DGORDER':
    case 'EXHOSLUSM495S':
    case 'CCSA':
    case 'ICSA':
    case 'WHLSLRIMSA':
    case 'RSAFS':
    case 'MARTSSM44W72USS':
      return value.div(1000);

    default:
      return value;
  }
}



const __fredCode2CalculatedUnit: Record<graphql.IndexFredCode, string> = {
  A191RL1Q225SBEA: '%',
  ADXTNO: 'B',
  CCSA: 'K',
  CES0500000003: '%',
  CIVPART: '%',
  COMPQ: '',
  CPIAUCSL: '%',
  CPILFESL: '%',
  CSUSHPISA: '%',
  DGORDER: 'B',
  DGS1: '%',
  DGS10: '%',
  DGS1MO: '%',
  DGS2: '%',
  DGS20: '%',
  DGS3: '%',
  DGS30: '%',
  DGS3MO: '%',
  DGS5: '%',
  DGS6MO: '%',
  DGS7: '%',
  DPCCRV1Q225SBEA: '%',
  DSPIC96: '%',
  EXHOSLUSM495S: 'K',
  GDP: '%',
  GDPA: '%',
  HOUST: 'K',
  HPIPONM226S: '',
  ICSA: 'K',
  INDPRO: '',
  JTSJOL: 'K',
  LES1252881600Q: '',
  MARTSSM44W72USS: 'B',
  MEPAINUSA672N: '',
  MORTGAGE30US: '%',
  OPHNFB: '',
  PAYEMS: 'K',
  PCE: '%',
  PCEPI: '%',
  PCEPILFE: '%',
  PERMIT: 'K',
  PPIACO: '',
  PSAVERT: '%',
  RSAFS: 'B',
  SPX: '',
  TCU: '%',
  U6RATE: '%',
  UMCSENT: '',
  UNRATE: '%',
  USSTHPI: '',
  WHLSLRIMSA: 'B',
  WPSFD4131: '%',
  WPUFD49207: '%'
};

const __fredCode2QueryString: Partial<Record<graphql.IndexFredCode, string>> = {
  PCE: '?g=1IrOw',
  DSPIC96: '?g=1IrPE',
  CES0500000003: '?g=1HYxN',
  CPIAUCSL: '?g=1IrQe',
  CPILFESL: '?g=1IrQo',
  PCEPI: '?g=1IrQA',
  PCEPILFE: '?g=1IrQI',
  WPUFD49207: '?g=1IrSN',
  WPSFD4131: '?g=1IrSG',
  CSUSHPISA: '?g=1FIHR',
  GDP: '?g=1Is4C',
  GDPA: '?g=1HjUu'
};

function __isPercentage(fredCode: graphql.IndexFredCode) {
  switch (fredCode) {
    // case 'A191RL1Q225SBEA':
    // case 'CES0500000003':
    // case 'CIVPART':
    case 'CPIAUCSL':
    case 'CPILFESL':
    // case 'CSUSHPISA':
    // case 'DGS1':
    // case 'DGS10':
    // case 'DGS1MO':
    // case 'DGS2':
    // case 'DGS20':
    // case 'DGS3':
    // case 'DGS30':
    // case 'DGS3MO':
    // case 'DGS5':
    // case 'DGS6MO':
    // case 'DGS7':
    // case 'DPCCRV1Q225SBEA':
    // case 'DSPIC96':
    // case 'GDP':
    // case 'GDPA':
    // case 'MORTGAGE30US':
    case 'PCE':
    case 'PCEPI':
    case 'PCEPILFE':
    // case 'PSAVERT':
    // case 'TCU':
    // case 'U6RATE':
    // case 'UNRATE':
    case 'WPSFD4131':
    case 'WPUFD49207':
      return true;
  }

  return false;
}
